import { useEffect, useState } from "react";

import { HotkeyDecorator } from "@/components/atoms/HotkeyDecorator";
import { SelectableCardListSearchField } from "@/components/molecules/SelectableCardList";
import { Dialog } from "@/components/organisms/Dialog";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";

import { TriageTabOptionsPanel } from "./TriageTabOptionsPanel";
import {
  type TriageDetail,
  useTriageDetailContext,
} from "./TriageView.context";
import {
  TRIAGE_RESOLUTION_TAB_LABELS,
  TRIAGE_TAB_CATALOG_CONFIG,
  type TriageResolutionTab,
} from "./TriageView.resolution";

/** What each list is for, said under its name. */
const TRIAGE_RECONCILE_MANUALLY_DESCRIPTIONS: Partial<
  Record<TriageResolutionTab, string>
> = {
  transactions: "Other payments to match this one against.",
  document: "Receipts, bills and invoices to settle with it.",
  categorize: "An account of the chart to post it to.",
};

/**
 * A list picked from by hand - the business's transactions, documents or
 * accounts - opened from a card of the suggestions: by its ⋯, or its way to
 * all it searches. What is picked here is picked for the
 * payment as a suggestion's lines are, and the footer's Book books it; Done
 * puts the list away.
 */
export function TriageReconcileManuallyDialog({
  triage,
  entityIdentifiers,
}: {
  triage: TriageDetail;
  entityIdentifiers: string[];
}) {
  const {
    reconcilingManually: tab,
    setReconcilingManually,
    activeOptionKey,
    setActiveOptionKey,
  } = useTriageDetailContext();
  const [search, setSearch] = useState("");
  // Each list opens on all of it.
  useEffect(() => setSearch(""), [tab]);
  // The stop it was opened from - the keyboard on it as it opened.
  const [openedFrom, setOpenedFrom] = useState<string | null>(null);
  const [wasOpen, setWasOpen] = useState(tab);
  if (tab !== wasOpen) {
    setWasOpen(tab);
    if (tab) setOpenedFrom(activeOptionKey);
  }
  // Put away, the keyboard is back on the stop that opened it.
  const close = () => {
    if (openedFrom) setActiveOptionKey(openedFrom);
    setReconcilingManually(null);
  };

  return (
    <Dialog
      isOpen={tab != null}
      onClose={close}
      isConstrained
      renderKey="triage-reconcile-manually"
    >
      {tab ? (
        <div
          data-testid="triage-reconcile-manually-dialog"
          data-tab={tab}
          // A list's own height on a desktop, scrolled within; a phone's
          // dialog is the screen already.
          className="flex flex-col min-h-0 h-auto md:h-[70vh]"
        >
          <div className="pl-5 pr-5 pt-5 pb-3">
            <Typography level="title-lg">
              {TRIAGE_RESOLUTION_TAB_LABELS[tab]}
            </Typography>
            <Typography
              level="body-sm"
              className="text-muted-foreground/80 mt-1"
            >
              {TRIAGE_RECONCILE_MANUALLY_DESCRIPTIONS[tab]}
            </Typography>
          </div>
          <SelectableCardListSearchField
            align="flush"
            placeholder={TRIAGE_TAB_CATALOG_CONFIG[tab].searchPlaceholder}
            value={search}
            onChange={setSearch}
            inputTestId="triage-reconcile-manually-search"
            // Ruled off from the list it searches, as the footer is.
            containerClassName="[border-bottom-width:1px] [border-bottom-style:solid] border-border"
          />
          <div
            // The match panel's ground, so the row the keys are on stands
            // off it as it does there.
            className="flex-1 min-h-0 flex flex-col bg-muted dark:bg-[#151719]"
          >
            <TriageTabOptionsPanel
              triage={triage}
              entityIdentifiers={entityIdentifiers}
              tab={tab}
              search={search}
            />
          </div>
          <div className="flex justify-end gap-2 p-3 [border-top-width:1px] [border-top-style:solid] border-border">
            <Button
              data-testid="triage-reconcile-manually-done"
              data-context-overlay-submit=""
              onClick={close}
            >
              Done
              <HotkeyDecorator hotkey="escape" color="inherit" />
            </Button>
          </div>
        </div>
      ) : (
        <></>
      )}
    </Dialog>
  );
}
